<template>
  <div class="subpage-wrapper">
    <transition name="fade">
      <div class="subpage-container" v-if="show">
        <div class="type-selector-container">
          <CustomSelect
            v-model="selectedPromptSet"
            :options="promptSets"
            :disabled="isSubmittingPrompt"
          />
          <CustomSelect
            v-model="selectedPromptType"
            :options="promptTypes"
            :disabled="isSubmittingPrompt"
            style="margin-left: 0.4em;"
          />
        </div>
        <div
          ref="promptListContainer"
          class="prompt-list-container"
        >
          <div class="prompt-list">
            <div 
              ref="topRow"
              :class="[{ 'prompt-item-property-hidden': isCreatingPrompt || isEditingPrompt }, 'prompt-item', 'top-row']" 
            >
              <div class="prompt-item-property-text"> Prompt </div>
              <div class="prompt-item-property-type"> Actions </div>
              <div class="prompt-item-property-type"> Set </div>
              <div class="prompt-item-property-type"> Reply Type </div>
              <div class="prompt-item-property-type"> Last Edited </div>
              <div class="prompt-item-property-type"> Active </div>
              <div class="prompt-item-property-overflow"></div>
            </div>

            <div class="prompt-item" v-show="!isCreatingPrompt">
              <SpinnerButton
                class="prompt-item-property-text prompt-item-to-create"
                :isProcessing="isSubmittingPrompt"
                :showSpinner="false"
                @click="startCreatingPrompt"
              >
                + Type in a new prompt to add
              </SpinnerButton>
            </div>
            
            <div 
              class="prompt-item"
              v-show="isCreatingPrompt"
            >
              <div class="prompt-item-property-text">
                <input
                  ref="createTextInput"
                  class="create-custom-prompt-to-create-text static"
                  v-model="customPromptToCreate.text"
                  :disabled="isSubmittingPrompt"
                  placeholder="Type in your prompt here..."
                  @keydown.enter.prevent="createCustomPrompt"
                />
                <span :class="[{ invalid: customPromptToCreate.text.length > characterLimit }, 'create-custom-prompt-character-limit']">
                  {{ customPromptToCreate.text.length }} / {{ characterLimit }} characters
                </span>
              </div>
              <div class="prompt-item-property-type"> Custom </div>
              <div class="prompt-item-property-type">
                <CustomSelect
                  ref="customPromptReplySelector"
                  v-model="customPromptToCreate.reply"
                  :options="['text', 'gif']"
                  :disabled="isSubmittingPrompt"
                />
              </div>
              <div class="prompt-item-property-create-custom button">
                <SpinnerButton
                  class="create-custom-prompt-button"
                  :isDisabled="customPromptToCreate.text.length < 1 || customPromptToCreate.text.length > characterLimit"
                  :isProcessing="isSubmittingPrompt"
                  @click="createCustomPrompt"
                >
                  Create Prompt
                </SpinnerButton>
              </div>
              <div class="prompt-item-property-cancel-custom button">
                <SpinnerButton
                  class="create-custom-prompt-cancel-button"
                  :isProcessing="isSubmittingPrompt"
                  :showSpinner="false"
                  @click="stopCreatingPrompt"
                >
                  Cancel
                </SpinnerButton>
              </div>
            </div>

            <div
              class="prompt-item"
              v-for="(prompt, promptIndex) in customPromptsToDisplay"
              :key="'c'+promptIndex"
            >
              <!-- isEditing is false -->
              <div 
                :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-text']"
                v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"
              >
                {{ prompt.text }}
              </div>

              <div 
                class="prompt-item-property-type"
                v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false">
                <SpinnerButton
                  class="prompt-item-button edit"
                  :isProcessing="isSubmittingPrompt"
                  :showSpinner="false"
                  @click="editCustomPrompt(promptIndex)"
                >
                  Edit
                </SpinnerButton>
              </div>

              <div 
                :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']"
                v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"
              >
                Custom 
              </div>

              <div
                :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']"
                v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"
              >
                {{ prompt.reply }} 
              </div>
              
              <div 
                :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-last-edited']"
                v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"
              >
                {{ new Date(prompt.lastEditedTimestamp).toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' }) }} 
                <br>
                {{ prompt.editedByFirstName }}
              </div>

              <toggle-button
                :value="false"
                v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"
                color="#00B4A3"
                :sync="true"
                :height="toggleHeight"
                :width="toggleWidth"
                v-model="prompt.active"
                :disabled="isSubmittingPrompt"
                class="prompt-item-property-type"
                style="display: flex; align-items: center;"
                @change="togglePromptActivity('CUSTOM', customPromptsToDisplay, promptIndex)"
              />

              <div 
                class="prompt-item-property-overflow" 
                v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"
              >
                <PromptItemOverflow 
                  ref="customOverflow"
                  @opened="stopCreatingPrompt(); stopEditingCustomPrompt(promptIndexBeingEdited);"
                  :disabled="isSubmittingPrompt"
                >
                  <template v-slot:dropdown>
                    <ul class="dropdown">
                      <li>
                        <a @click="$refs['customOverflow'][promptIndex].close(); openAddToPackModal('CUSTOM', customPromptsToDisplay, promptIndex);">
                          Add to Prompt Pack
                        </a>
                      </li>
                      <li>
                        <a @click="$refs['customOverflow'][promptIndex].close(); duplicatePrompt(customPromptsToDisplay, promptIndex);">
                          Duplicate
                        </a>
                      </li>
                      <li>
                        <a @click="$refs['customOverflow'][promptIndex].close(); deleteCustomPrompt(promptIndex);" 
                        class="text-danger">
                          Delete
                        </a>
                      </li>
                    </ul>
                  </template>
                </PromptItemOverflow>
              </div>

              <!-- isEditing is true -->
              <div 
                class="prompt-item-property-text"
                v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
              >
                <input
                  ref="customTextInput"
                  class="create-custom-prompt-to-create-text static"
                  v-model="prompt.text"
                  :disabled="isSubmittingPrompt"
                  @keydown.enter.prevent="updateCustomPrompt(customPromptsToDisplay, promptIndex)"
                />
                <span :class="[{ invalid: prompt.text.length > characterLimit }, 'create-custom-prompt-character-limit']">
                  {{ prompt.text.length }} / {{ characterLimit }} characters
                </span>
              </div>
              <div 
                class="prompt-item-property-type"
                v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
              > 
                Custom 
              </div>
              <div 
                class="prompt-item-property-type"
                v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
              >
                <CustomSelect
                  ref="customReplySelect"
                  v-model="prompt.reply"
                  :options="['text', 'gif']"
                  :default="prompt.reply"
                  :disabled="isSubmittingPrompt"
                />
              </div>
              <div
                class="prompt-item-property-create-custom"
                v-show="'isEditing' in prompt && prompt.isEditing === true"
              >
                <SpinnerButton
                  class="create-custom-prompt-button"
                  :isProcessing="isSubmittingPrompt"
                  :isDisabled="prompt.text.length < 1 || prompt.text.length > characterLimit"
                  @click="updateCustomPrompt(customPromptsToDisplay, promptIndex)"
                >
                  Update
                </SpinnerButton>
              </div>
              <div
                class="prompt-item-property-cancel-custom"
                v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
              >
                <SpinnerButton
                  class="create-custom-prompt-cancel-button"
                  :isProcessing="isSubmittingPrompt"
                  :showSpinner="false"
                  @click="stopEditingCustomPrompt(promptIndex)"
                >
                  Cancel
                </SpinnerButton>
              </div>
            </div>

            <div
              class="prompt-item"
              v-for="(prompt, promptIndex) in publicPromptsToDisplay"
              :key="'p'+promptIndex"
            >
              <div :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-text']">
                {{ prompt.text }} 
              </div>
              <div class="prompt-item-property-type" />
              <div :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']">
                Default 
              </div>
              <div :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']">
                {{ prompt.reply }} 
              </div>
              <div class='prompt-item-property-type'>
                -------------
              </div>
              <toggle-button
                :value="true"
                color="#00B4A3"
                :sync="true"
                :height="toggleHeight"
                :width="toggleWidth"
                v-model="prompt.active"
                :disabled="isSubmittingPrompt"
                class="prompt-item-property-type"
                style="display: flex; align-items: center;"
                @change="togglePromptActivity('PUBLIC', publicPromptsToDisplay, promptIndex)"
              />
              <div class="prompt-item-property-overflow">
                <PromptItemOverflow 
                  ref="publicOverflow"
                  @opened="stopCreatingPrompt(); stopEditingCustomPrompt(promptIndexBeingEdited);"
                  :disabled="isSubmittingPrompt"
                >
                  <template v-slot:dropdown>
                    <ul class="dropdown">
                      <li>
                        <a @click="$refs['publicOverflow'][promptIndex].close(); openAddToPackModal('PUBLIC', publicPromptsToDisplay, promptIndex);">
                          Add to Prompt Pack
                        </a>
                      </li>
                      <li>
                        <a @click="$refs['publicOverflow'][promptIndex].close(); duplicatePrompt(publicPromptsToDisplay, promptIndex);">
                          Duplicate
                        </a>
                      </li>
                    </ul>
                  </template>
                </PromptItemOverflow>
              </div>
            </div>
          </div>
          <div class="padding-row">
          </div>
        </div>
      </div>
    </transition>
    
    <div class="subpage-container" v-if="!show">
      <div class="spinner-top-padded">
        <Spinner color="teal" />
      </div>
    </div>

    <div class="right-subpage-container" />
    <AddToPackModal
      v-if="showAddToPackModal"
      :promptPacks="promptPacks"
      :promptSet="promptSetToAdd"
      :promptId="promptsToLookThrough[promptIndexToAdd].promptId"
      style="z-index: 100;"
      @close="showAddToPackModal = false"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import PromptItemOverflow from '@/components/promptViewComponents/PromptItemOverflow.vue';
import AddToPackModal from '@/components/promptViewComponents/AddToPackModal.vue';
import CustomSelect from '@/components/utilComponents/CustomSelect.vue';
import Spinner from '@/components/utilComponents/Spinner';
import SpinnerButton from '@/components/utilComponents/SpinnerButton'
import { ToggleButton } from 'vue-js-toggle-button';

export default Vue.extend({
  name: 'PromptLibraryView',
  components: {
    PromptItemOverflow,
    AddToPackModal,
    CustomSelect,
    Spinner,
    SpinnerButton,
    ToggleButton
  },
  data: () => ({
    show: false,
    // these are lists of prompts
    publicPrompts: [],
    customPrompts: [],

    // used for creating/editing of custom prompts
    isCreatingPrompt: false,
    isEditingPrompt: false,
    isSubmittingPrompt: false,    // flag to make sure the user can't do anything if they are updating the db
    promptIndexBeingEdited: null, // keeps track of the custom prompt that is being edited
    characterLimit: 140,
    customPromptToCreate: {
      reply: 'text',
      type: 'static',
      text: '',
      injection: 'PLAYER_NAME'
    },

    // new selector for prompts to display
    promptSets: ['All Sets', 'Default', 'Custom'],
    selectedPromptSet: 'All Sets',
    promptTypes: ['All Reply Types', 'Text', 'Gif'],
    selectedPromptType: 'All Reply Types',

    // used for the prompt packs
    promptPacks: [],
    newPromptPack: '',

    // prompts to add to pack
    promptSetToAdd: '', 
    promptsToLookThrough: [],
    promptIndexToAdd: null,
    showAddToPackModal: false,

    // toggle button dimensions
    toggleHeight: 0,
    toggleWidth: 0
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    promptSetsToDisplay: function() {
      let toReturn = [];
      switch(this.selectedPromptSet) {
        case 'All Sets':
          toReturn = ['public', 'custom'];
          break;
        case 'Default':
          toReturn = ['public'];
          break;
        case 'Custom':
          toReturn = ['custom'];
          break;
      }
      return toReturn;
    },
    promptTypesToDisplay: function() {
      let toReturn = [];
      switch(this.selectedPromptType) {
        case 'All Reply Types':
          toReturn = ['text', 'gif'];
          break;
        case 'Text':
          toReturn = ['text'];
          break;
        case 'Gif':
          toReturn = ['gif'];
          break;
      }
      return toReturn;
    },
    publicPromptsToDisplay: function () {
      let prompts = []
      if (!this.promptSetsToDisplay.includes('public')) {
        return prompts;
      }
      this.publicPrompts.forEach(prompt => {
        if (this.promptTypesToDisplay.includes(prompt.reply)) {
          prompt.text = prompt.text.replace("%PLAYER_NAME%", "[Player.Name]");
          prompts.push(prompt);
        }
      });
      return prompts;
    },
    customPromptsToDisplay: function () {
      let prompts = []
      if (!this.promptSetsToDisplay.includes('custom')) {
        return prompts;
      }
      this.customPrompts.forEach(prompt => {
        if (this.promptTypesToDisplay.includes(prompt.reply)) {
          prompts.push(prompt);
        }
      });
      return prompts;
    },
  },
  methods: {
    async getPrompts() {
      await this.getPublicPrompts();
      await this.getCustomPrompts();
    },
    async getPublicPrompts() {
      try {
        const promptsResponse = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getprompts`, {
          auth0UserId: this.auth0UserId,
          promptSet: 'PUBLIC'
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.publicPrompts = promptsResponse.data.prompts;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async getCustomPrompts() {
      try {
        const promptsResponse = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getprompts`, {
          auth0UserId: this.auth0UserId,
          promptSet: 'CUSTOM'
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.customPrompts = promptsResponse.data.prompts;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async startCreatingPrompt() {
      await this.getCustomPrompts();
      this.$refs.promptListContainer.scrollTo(0, 0);
      this.isCreatingPrompt = true;
      this.$nextTick(() => {
        this.$refs.createTextInput.focus();
      });
    },
    stopCreatingPrompt() {
      if (this.isSubmittingPrompt) {
        return;
      }

      this.isCreatingPrompt = false;
      this.customPromptToCreate.type = 'static';
      this.customPromptToCreate.reply = 'text';
      this.customPromptToCreate.text = '';
      this.$refs['customPromptReplySelector'].selected = 'text'
    },
    async createCustomPrompt() {
      if (this.isSubmittingPrompt || !this.customPromptToCreate.text || this.customPromptToCreate.text.length > this.characterLimit) {
        return;
      }

      this.isSubmittingPrompt = true;

      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/createcustomprompt`, {
          auth0UserId: this.auth0UserId,
          promptType: this.customPromptToCreate.type,
          replyType: this.customPromptToCreate.reply,
          promptText: this.customPromptToCreate.text,
          injections: [this.customPromptToCreate.injection]
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log('Error:', error);
      }
      this.isSubmittingPrompt = false;
      this.stopCreatingPrompt();
      await this.getCustomPrompts();
    },
    async deleteCustomPrompt(promptIndex) {
      if (this.isSubmittingPrompt) {
        return;
      }

      this.isSubmittingPrompt = true;
      
      try {
        const promptId = this.customPromptsToDisplay[promptIndex].promptId;
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/deletecustomprompt`, {
          auth0UserId: this.auth0UserId,
          promptId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log('Error:', error);
      }

      await this.getCustomPrompts();
      this.isSubmittingPrompt = false;
    },
    async editCustomPrompt(promptIndex) {
      await this.getCustomPrompts();
      this.closeAllPromptOverflows();
      this.stopCreatingPrompt();
      this.isEditingPrompt = true;
      this.promptIndexBeingEdited = promptIndex;
      const selectedPrompt = this.customPromptsToDisplay[promptIndex];
      this.$set(selectedPrompt, 'isEditing', true);
      this.$nextTick(() => {
        this.$refs['customTextInput'][promptIndex].focus();
        this.$refs['customReplySelect'][promptIndex].selected = this.customPromptsToDisplay[promptIndex].reply;
      });
    },
    async stopEditingCustomPrompt(promptIndex) {
      if (promptIndex === null || this.isSubmittingPrompt) {
        // use a null check here since the prompt index can also be 0
        return;
      }

      await this.getCustomPrompts();
      const selectedPrompt = this.customPromptsToDisplay[promptIndex];
      this.$set(selectedPrompt, 'isEditing', false);
      this.isEditingPrompt = false;
      this.promptIndexBeingEdited = null;
    },
    async updateCustomPrompt(prompts, promptIndex) {
      if(this.isSubmittingPrompt || !prompts[promptIndex].text.length || prompts[promptIndex].text.length > this.characterLimit) {
        return;
      }

      this.isSubmittingPrompt = true;

      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/editcustomprompt`, {
          auth0UserId: this.auth0UserId,
          promptId: prompts[promptIndex].promptId,
          promptType: prompts[promptIndex].type,
          replyType: prompts[promptIndex].reply,
          promptText: prompts[promptIndex].text,
          injections: '',
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log(error);
      }

      await this.getCustomPrompts();
      this.isSubmittingPrompt = false;
      this.isEditingPrompt = false;
    },
    async togglePromptActivity (promptSet, prompts, promptIndex) {
      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/togglepromptactivity`, {
          auth0UserId: this.auth0UserId,
          promptSet,
          promptId: prompts[promptIndex].promptId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
    async openAddToPackModal(promptSet, prompts, promptIndex) {
      this.promptSetToAdd = promptSet, 
      this.promptsToLookThrough = prompts,
      this.promptIndexToAdd = promptIndex
      this.showAddToPackModal = true;
    },
    async getPromptPacks() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getpromptpacks`, {
          auth0UserId: this.auth0UserId,
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.promptPacks = response.data.promptPacks;
      } catch (error) {
        console.log(error);
      }
    },
    async duplicatePrompt (prompts, promptIndex) {
      this.customPromptToCreate.type = 'static';
      this.customPromptToCreate.reply = prompts[promptIndex].reply;
      this.customPromptToCreate.text = prompts[promptIndex].text;
      await this.startCreatingPrompt();
    },
    calcToggleSize() {
      this.toggleHeight = Math.min(document.documentElement.clientHeight, document.documentElement.clientWidth) / 33;
      this.toggleWidth = this.toggleHeight * 2.27;
    },
    closeAllPromptOverflows() {
      this.$refs['customOverflow'].forEach(overflow => {
        overflow.close();
      });
      this.$refs['publicOverflow'].forEach(overflow => {
        overflow.close();
      });
    }
  },
  mounted: async function() {
    this.$nextTick(function() {
      window.addEventListener('resize', this.calcToggleSize);
      this.calcToggleSize();
    });
    try {
      await this.getPrompts();
      await this.getPromptPacks();
      this.show = true;
      
    } catch (error) {
      console.log('Error:', error);
      console.log(error.body);
    }
  }
})
</script>

<style scoped lang="scss">
.subpage-wrapper {
  display: flex;
  max-height: 100%;
}

.subpage-container {
  margin: 0 auto;
  width: 60em;
}

.right-subpage-container {
  padding-top: 4em;
  width: 11em;
  margin-left: 0.4em;
}

.type-selector-container {
  display: flex;
  width: 50%;
  margin-left: 0.8em;
  margin-bottom: 0.4em;
  font-size: 0.8em;
}

.prompt-list-container {
  height: 80vh;
  width: 100%;
  overflow: scroll;
  overflow-x: hidden;
  list-style-type: none;
  font-size: 0.8em;
  scroll-behavior: smooth;
}

.prompt-list {
  position: relative;
  border-radius: 0.4em;
  color: $smoke-gray;
}

.prompt-item {
  position: relative;
  display: flex;
  margin: 0 0.8em;
  padding: 0.4em 1.6em;
  border: 0.1em solid $light-gray;
  height: 4.4em;
  color: $smoke-gray;
  background-color: white;
  
  &-button {
    display: flex;
    justify-content: center;
    height: max-content;
    width: max-content;
    background-color: $brand-gold;
    text-align: center;
    padding: 0.4em 0.8em;
    margin: 0.4em auto;
    font-size: 1.2em;
    border-radius: 0.2em;
    box-shadow: 0 1px 1px 1px $light-gray;
    user-select: none;
    cursor: pointer;

    &.edit {
      color: $natural-white;
      background-color: $lilac-purple;
      z-index: 10;
    }
    &.danger {
      color: $natural-white;
      background-color: $crimson;
      z-index: 10;
    }
    &.danger:hover {
      color: $natural-white;
      background-color: $light-crimson;
      z-index: 10;
    }
  }

  &-button:hover {
    background-color: $light-lilac-purple;
  }

  &-to-create {
    color: $brand-teal;
    &:hover {
      color: $light-teal!important;
      cursor: pointer;
    }
  }

  &-property {
    padding: 0.4em 0.8em;
    justify-content: center;
    
    &-text {
      display: flex;
      align-items: center;
      flex: 2;
      width: 50%;
      left: 25%;
      user-select: none;
      transition: color 0.5s;
      white-space: nowrap;
      overflow-x: auto;
    }
    &-hidden {
      color: $off-white;
    }
    &-type {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 10%;
      text-transform: capitalize;
      user-select: none;
      transition: color 0.5s;
    }
    &-last-edited {
      display: block;
      align-items: center;
      width: 10%;
      padding-top: 0.4em;
      text-align: center;
      user-select: none;
      transition: color 0.5s;
      white-space: nowrap; 
      overflow: hidden;
      text-overflow: ellipsis; 
    }
    &-overflow {
      display: flex;
      justify-content: center;
      width: 2%;
      margin-left: 0.4em;
    }
    &-create-custom {
      display: flex;
      justify-content: center;
    }
    &-cancel-custom {
      display: flex;
      justify-content: center;
      width: 6%;
    }
  }
}

.top-row {
  font-weight: bold;
  border-radius: 0.8em 0.8em 0 0;
}

.prompt-item:last-of-type {
  border-radius: 0 0 0.8em 0.8em;
}

.inactive {
  color: $light-gray;
}

.create-custom-prompt {
  &-button {
    display: flex;
    justify-content: center;
    height: max-content;
    background-color: $brand-teal;
    color: $natural-white;
    text-align: center;
    padding: 0.4em 0.6em;
    margin: 0.2em 0.6em;
    font-size: 1.2em;
    border-radius: 0.2em;
    box-shadow: 0 1px 1px 1px $light-gray;
    user-select: none;
    cursor: pointer;    
  }
  &-button:hover {
    // filter: brightness(1.15);
    background-color: $light-teal;
  }
  &-cancel-button {
    display: flex;
    justify-content: center;
    height: max-content;
    background-color: $crimson;
    color: $natural-white;
    text-align: center;
    padding: 0.4em 0.6em;
    margin: 0.2em 0.4em;
    font-size: 1.2em;
    border-radius: 0.2em;
    box-shadow: 0 1px 1px 1px $light-gray;
    user-select: none;
    cursor: pointer;  
  }
  &-cancel-button:hover {
    background-color: $light-crimson;
  }
  &-to-create-text {
    &.static {
      width: 100%;
      font-size: 1em;
      font-family: inherit;
      border: 0.1em solid $brand-teal;
      border-radius: 0.4em;
      padding: 0.5em 0.8em;
    }
  }
  &-character-limit {
    font-size: 0.8em;
    padding: 0.4em 0.8em;
    text-align: center;

    &.invalid {
      color: $crimson;      
    }
  }
}

.dropdown {
  position: absolute;
  right: 0px;
  z-index: 20;
  background: white;
  border: 1px solid $smoke-gray;
  border-radius: 0.4em;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16), 0 2px 8px 0 rgba(0, 0, 0, 0.12);
  margin: 0;
  padding: 0;

  li {
    display: block;
    width: 100%;
    padding: 0;
    margin: 0;
    a {
      width: 100%;
      padding: 1em 18px;
      margin: 0;
      display: inline-block;
      white-space: pre;
      box-sizing: border-box;
      user-select: none;
      &:hover {
        background: $lilac-purple;
        color: white;
        cursor: pointer;
      }
    }
  }
}

.text-danger {
  color: $crimson;
}

.padding-row {
  height: 14em;
}

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  text-align: center;
  user-select: none;
}
</style>
